import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, ChevronDown, ChevronRight, Shield, Heart,
  AlertTriangle, Users, Target, Sparkles, Info,
  Lock, Eye, Zap
} from 'lucide-react';

// Early Maladaptive Schemas (EMS) based on Jeffrey Young's Schema Therapy
// Organised by schema domains
const SCHEMA_DOMAINS = {
  disconnection: {
    label: 'Disconnection & Rejection',
    icon: <Heart className="w-5 h-5" />,
    colour: 'bg-red-500/20 border-red-500',
    description: 'Expectations that needs for security, safety, acceptance and respect won\'t be met',
    schemas: [
      {
        id: 'abandonment',
        name: 'Abandonment / Instability',
        belief: 'People I care about will leave me or be unpredictable',
        origin: 'Often develops when caregivers were unstable, unreliable, or emotionally unavailable',
        triggers: ['Partner being late', 'Friends cancelling plans', 'Changes in routine'],
        coping: ['Clinginess', 'Jealousy', 'Avoiding attachment', 'Testing relationships'],
        healthyAlternative: 'Some relationships end, but I can build stable, lasting connections',
        questions: [
          'Do you often feel anxious that people will leave you?',
          'Do you need constant reassurance in relationships?',
          'Do you push people away before they can abandon you?'
        ]
      },
      {
        id: 'mistrust',
        name: 'Mistrust / Abuse',
        belief: 'Others will hurt, abuse, humiliate, or take advantage of me',
        origin: 'Often develops from experiences of abuse, betrayal, or manipulation',
        triggers: ['Authority figures', 'Vulnerability', 'Trusting others'],
        coping: ['Hypervigilance', 'Avoidance of intimacy', 'Testing people', 'Aggression'],
        healthyAlternative: 'While some people aren\'t trustworthy, many are, and I can learn to identify safe people',
        questions: [
          'Do you expect others to hurt or betray you?',
          'Is it hard for you to trust people, even those close to you?',
          'Do you often feel you need to protect yourself from others?'
        ]
      },
      {
        id: 'emotional_deprivation',
        name: 'Emotional Deprivation',
        belief: 'My emotional needs won\'t be adequately met by others',
        origin: 'Often develops when caregivers didn\'t provide adequate nurturing, empathy, or protection',
        triggers: ['Asking for help', 'Expressing needs', 'Intimate moments'],
        coping: ['Not expressing needs', 'Withdrawal', 'Demanding behaviour', 'Feeling empty'],
        healthyAlternative: 'I deserve emotional support and can find people who can meet my needs',
        questions: [
          'Do you often feel that no one really understands you?',
          'Do you feel like your emotional needs are rarely met?',
          'Do you avoid asking for emotional support because you expect disappointment?'
        ]
      },
      {
        id: 'defectiveness',
        name: 'Defectiveness / Shame',
        belief: 'I am flawed, bad, unwanted, or inferior in important respects',
        origin: 'Often develops from critical, rejecting, or shaming caregivers',
        triggers: ['Criticism', 'Comparisons', 'Intimacy', 'Being seen'],
        coping: ['Hiding perceived flaws', 'Self-criticism', 'Avoiding relationships', 'Overcompensating'],
        healthyAlternative: 'I have both strengths and weaknesses like everyone - this doesn\'t make me defective',
        questions: [
          'Do you feel fundamentally flawed or unlovable?',
          'Are you afraid others will see the "real" you and reject you?',
          'Do you often feel shame about who you are?'
        ]
      },
      {
        id: 'social_isolation',
        name: 'Social Isolation / Alienation',
        belief: 'I am fundamentally different from others and don\'t belong',
        origin: 'Often develops from feeling different during childhood or being excluded',
        triggers: ['Social gatherings', 'Group activities', 'Comparisons'],
        coping: ['Avoiding social situations', 'Staying on the outside', 'Feeling like an outsider'],
        healthyAlternative: 'Everyone feels different sometimes, but I can find my community and belong',
        questions: [
          'Do you feel like you don\'t fit in anywhere?',
          'Do you feel fundamentally different from most people?',
          'Do you often feel alone even in groups?'
        ]
      }
    ]
  },
  autonomy: {
    label: 'Impaired Autonomy & Performance',
    icon: <Shield className="w-5 h-5" />,
    colour: 'bg-orange-500/20 border-orange-500',
    description: 'Expectations about self and environment that interfere with independence and confidence',
    schemas: [
      {
        id: 'dependence',
        name: 'Dependence / Incompetence',
        belief: 'I am unable to handle everyday responsibilities without help',
        origin: 'Often develops when caregivers were overprotective or undermined the child\'s competence',
        triggers: ['New tasks', 'Decisions', 'Being alone', 'Challenges'],
        coping: ['Seeking excessive help', 'Avoiding responsibility', 'Learned helplessness'],
        healthyAlternative: 'I can develop competence and handle challenges - I\'m more capable than I think',
        questions: [
          'Do you feel unable to cope with everyday problems without help?',
          'Do you rely heavily on others to make decisions?',
          'Do you feel incompetent compared to others?'
        ]
      },
      {
        id: 'vulnerability',
        name: 'Vulnerability to Harm',
        belief: 'Catastrophe is about to strike and I can\'t prevent it',
        origin: 'Often develops from anxious caregivers or traumatic events',
        triggers: ['Uncertainty', 'Health symptoms', 'News', 'Travel'],
        coping: ['Excessive worry', 'Avoidance', 'Hypervigilance', 'Seeking reassurance'],
        healthyAlternative: 'While bad things can happen, most feared events don\'t occur, and I can cope with challenges',
        questions: [
          'Do you often worry that catastrophe is about to strike?',
          'Do you feel the world is a dangerous place?',
          'Do you take excessive precautions against unlikely dangers?'
        ]
      },
      {
        id: 'enmeshment',
        name: 'Enmeshment / Undeveloped Self',
        belief: 'I don\'t have my own identity separate from others',
        origin: 'Often develops when caregivers were enmeshed and didn\'t allow individuation',
        triggers: ['Being alone', 'Making independent choices', 'Disagreeing with others'],
        coping: ['Mirroring others', 'Difficulty with boundaries', 'Identity confusion'],
        healthyAlternative: 'I am my own person with my own identity, thoughts, and feelings',
        questions: [
          'Do you struggle to know who you are separate from others?',
          'Do you feel empty when you\'re not in a close relationship?',
          'Do you absorb other people\'s emotions and problems as your own?'
        ]
      },
      {
        id: 'failure',
        name: 'Failure',
        belief: 'I have failed or will inevitably fail; I am inadequate compared to peers',
        origin: 'Often develops from critical parents, being unfavourably compared, or academic/athletic failures',
        triggers: ['Performance situations', 'Comparisons', 'New challenges'],
        coping: ['Avoiding challenges', 'Self-sabotage', 'Overworking', 'Giving up easily'],
        healthyAlternative: 'Failure is part of learning and doesn\'t define my worth or future potential',
        questions: [
          'Do you believe you\'re less successful than your peers?',
          'Do you avoid situations where you might fail?',
          'Do you feel like a failure even when you achieve things?'
        ]
      }
    ]
  },
  limits: {
    label: 'Impaired Limits',
    icon: <Target className="w-5 h-5" />,
    colour: 'bg-yellow-500/20 border-yellow-500',
    description: 'Difficulty with self-control, limits, and responsibility to others',
    schemas: [
      {
        id: 'entitlement',
        name: 'Entitlement / Grandiosity',
        belief: 'I am superior and entitled to special privileges and treatment',
        origin: 'Often develops from being spoiled, not given limits, or compensating for deprivation',
        triggers: ['Not getting your way', 'Rules', 'Others\' needs', 'Criticism'],
        coping: ['Demanding behaviour', 'Disregarding others', 'Difficulty with boundaries'],
        healthyAlternative: 'I have equal worth to others - not more, not less',
        questions: [
          'Do you believe you deserve special treatment?',
          'Do you have difficulty accepting "no"?',
          'Do you struggle to consider others\' needs?'
        ]
      },
      {
        id: 'insufficient_self_control',
        name: 'Insufficient Self-Control',
        belief: 'I cannot or will not control my impulses or tolerate frustration',
        origin: 'Often develops when limits weren\'t set or modelled',
        triggers: ['Frustration', 'Temptation', 'Discomfort', 'Boring tasks'],
        coping: ['Impulsivity', 'Avoidance of discomfort', 'Giving up easily'],
        healthyAlternative: 'I can develop self-control and delay gratification for long-term goals',
        questions: [
          'Do you struggle to control your impulses?',
          'Do you have difficulty persisting at tasks that aren\'t immediately rewarding?',
          'Do you often give in to short-term gratification despite long-term consequences?'
        ]
      }
    ]
  },
  other_directedness: {
    label: 'Other-Directedness',
    icon: <Users className="w-5 h-5" />,
    colour: 'bg-blue-500/20 border-blue-500',
    description: 'Excessive focus on others\' needs at the expense of your own',
    schemas: [
      {
        id: 'subjugation',
        name: 'Subjugation',
        belief: 'I must submit to others\' control to avoid anger or abandonment',
        origin: 'Often develops when expressing needs led to punishment or rejection',
        triggers: ['Conflict', 'Expressing opinions', 'Saying no', 'Disagreements'],
        coping: ['People-pleasing', 'Suppressing needs', 'Passive-aggressive behaviour'],
        healthyAlternative: 'My needs and opinions are as valid as others\'; expressing them is healthy',
        questions: [
          'Do you suppress your own needs and preferences to please others?',
          'Do you feel trapped or controlled in relationships?',
          'Do you have difficulty expressing anger or disagreement?'
        ]
      },
      {
        id: 'self_sacrifice',
        name: 'Self-Sacrifice',
        belief: 'I must meet others\' needs at the expense of my own',
        origin: 'Often develops when the child took on a caretaker role or felt responsible for others',
        triggers: ['Others\' pain', 'Saying no', 'Self-care', 'Receiving help'],
        coping: ['Caretaking', 'Ignoring own needs', 'Burnout', 'Resentment'],
        healthyAlternative: 'Caring for myself allows me to better care for others; I\'m worthy of care too',
        questions: [
          'Do you consistently put others\' needs before your own?',
          'Do you feel guilty when you take time for yourself?',
          'Do you often feel resentful but can\'t express it?'
        ]
      },
      {
        id: 'approval_seeking',
        name: 'Approval-Seeking',
        belief: 'Getting approval from others is essential to my sense of self',
        origin: 'Often develops when love was conditional on achievement or behaviour',
        triggers: ['Criticism', 'Not being noticed', 'Being ordinary', 'Making choices'],
        coping: ['Seeking validation', 'Status-seeking', 'Changing to please others'],
        healthyAlternative: 'My worth comes from within; I can value others\' opinions without needing constant approval',
        questions: [
          'Do you excessively seek approval and recognition?',
          'Is your self-esteem dependent on others\' reactions?',
          'Do you change your behaviour significantly to be liked?'
        ]
      }
    ]
  },
  overvigilance: {
    label: 'Overvigilance & Inhibition',
    icon: <Lock className="w-5 h-5" />,
    colour: 'bg-purple-500/20 border-purple-500',
    description: 'Excessive emphasis on control, self-denial, and suppressing spontaneity',
    schemas: [
      {
        id: 'negativity',
        name: 'Negativity / Pessimism',
        belief: 'Things will go wrong; the worst will happen',
        origin: 'Often develops from early experiences of loss, disappointment, or anxious modelling',
        triggers: ['Good events', 'Hope', 'Optimism from others', 'Planning'],
        coping: ['Excessive worry', 'Preparing for the worst', 'Dismissing positives'],
        healthyAlternative: 'Both positive and negative outcomes are possible; focusing on the worst isn\'t realistic',
        questions: [
          'Do you tend to focus on the negative aspects of situations?',
          'Do you expect things to go wrong even when they\'re going well?',
          'Do you have difficulty enjoying good things because you\'re waiting for problems?'
        ]
      },
      {
        id: 'emotional_inhibition',
        name: 'Emotional Inhibition',
        belief: 'I must suppress my emotions and spontaneity to avoid shame or loss of control',
        origin: 'Often develops when emotions were punished, dismissed, or led to negative consequences',
        triggers: ['Strong emotions', 'Vulnerability', 'Spontaneity', 'Joy'],
        coping: ['Suppressing feelings', 'Appearing "fine"', 'Intellectualising', 'Being overly rational'],
        healthyAlternative: 'Emotions are information and expressing them appropriately is healthy and connecting',
        questions: [
          'Do you have difficulty expressing or even feeling your emotions?',
          'Do you feel uncomfortable with spontaneity or playfulness?',
          'Do you suppress anger, sadness, or joy?'
        ]
      },
      {
        id: 'unrelenting_standards',
        name: 'Unrelenting Standards',
        belief: 'I must meet very high standards to avoid criticism and shame',
        origin: 'Often develops from perfectionistic, critical, or high-achieving family environments',
        triggers: ['Making mistakes', 'Imperfection', 'Criticism', 'Comparisons'],
        coping: ['Perfectionism', 'Overworking', 'Self-criticism', 'Procrastination'],
        healthyAlternative: 'Good enough is good enough; perfection is impossible and unnecessary',
        questions: [
          'Do you set excessively high standards for yourself?',
          'Do you feel you must be the best at what you do?',
          'Do you criticise yourself harshly for not meeting your standards?'
        ]
      },
      {
        id: 'punitiveness',
        name: 'Punitiveness',
        belief: 'People (including myself) deserve harsh punishment for mistakes',
        origin: 'Often develops from punitive, critical, or unforgiving caregivers',
        triggers: ['Mistakes', 'Imperfection', 'Others\' failures', 'Own failures'],
        coping: ['Self-punishment', 'Harsh criticism of self and others', 'Difficulty forgiving'],
        healthyAlternative: 'Mistakes are human; compassion is more effective than punishment for growth',
        questions: [
          'Do you believe in strict punishment for mistakes?',
          'Do you have difficulty forgiving yourself or others?',
          'Are you intolerant of people who don\'t meet your standards?'
        ]
      }
    ]
  }
};

interface SchemaExplorerProps {
  onSchemaSelect?: (schemas: string[]) => void;
  showAssessment?: boolean;
}

export function SchemaExplorer({ onSchemaSelect, showAssessment = true }: SchemaExplorerProps) {
  const [expandedDomain, setExpandedDomain] = useState<string | null>(null);
  const [expandedSchema, setExpandedSchema] = useState<string | null>(null);
  const [identifiedSchemas, setIdentifiedSchemas] = useState<string[]>([]);
  const [notes, setNotes] = useState<Record<string, string>>({});

  const toggleDomain = (domain: string) => {
    setExpandedDomain(expandedDomain === domain ? null : domain);
    setExpandedSchema(null);
  };

  const toggleSchema = (schemaId: string) => {
    setExpandedSchema(expandedSchema === schemaId ? null : schemaId);
  };

  const toggleIdentified = (schemaId: string) => {
    const updated = identifiedSchemas.includes(schemaId)
      ? identifiedSchemas.filter(s => s !== schemaId)
      : [...identifiedSchemas, schemaId];
    setIdentifiedSchemas(updated);
    onSchemaSelect?.(updated);
  };

  const getAllSchemas = () => {
    return Object.values(SCHEMA_DOMAINS).flatMap(domain => domain.schemas);
  };

  const getSchemaById = (id: string) => {
    return getAllSchemas().find(s => s.id === id);
  };

  return (
    <div className="space-y-6">
      {/* Introduction */}
      <Card className="bg-gradient-to-br from-purple-500/5 to-pink-500/5 border-purple-500/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-500" />
            Schema Explorer
          </CardTitle>
          <CardDescription>
            Schemas are deep patterns of thinking and feeling that develop in childhood and continue throughout life. 
            They're like "life traps" - once you understand them, you can start to change them. 
            This exploration is for awareness, not diagnosis.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <p className="text-xs text-muted-foreground">
                <strong className="text-foreground">Important:</strong> These patterns are common human experiences, 
                not disorders. Many people have several schemas. Understanding them is the first step to change. 
                For deeper work, consider working with a therapist trained in Schema Therapy.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Identified Schemas Summary */}
      {identifiedSchemas.length > 0 && (
        <Card className="border-primary/20">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Eye className="w-4 h-4" />
              Patterns You've Identified ({identifiedSchemas.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {identifiedSchemas.map(id => {
                const schema = getSchemaById(id);
                return schema ? (
                  <Badge key={id} variant="default" className="cursor-pointer" onClick={() => toggleIdentified(id)}>
                    {schema.name} ✕
                  </Badge>
                ) : null;
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Schema Domains */}
      <div className="space-y-3">
        {Object.entries(SCHEMA_DOMAINS).map(([key, domain]) => {
          const isExpanded = expandedDomain === key;
          const domainSchemaCount = domain.schemas.filter(s => 
            identifiedSchemas.includes(s.id)
          ).length;
          
          return (
            <Card 
              key={key}
              className={`transition-all ${isExpanded ? domain.colour : 'hover:border-primary/30'}`}
            >
              <CardHeader 
                className="pb-2 cursor-pointer"
                onClick={() => toggleDomain(key)}
              >
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    {domain.icon}
                    {domain.label}
                    {domainSchemaCount > 0 && (
                      <Badge variant="secondary" className="ml-2">
                        {domainSchemaCount} identified
                      </Badge>
                    )}
                  </CardTitle>
                  <ChevronDown className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                </div>
                {!isExpanded && (
                  <CardDescription className="text-xs">{domain.description}</CardDescription>
                )}
              </CardHeader>
              
              <AnimatePresence>
                {isExpanded && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                  >
                    <CardContent className="pt-0 space-y-4">
                      <p className="text-sm text-muted-foreground">{domain.description}</p>
                      
                      {/* Individual Schemas */}
                      {domain.schemas.map(schema => {
                        const isSchemaExpanded = expandedSchema === schema.id;
                        const isIdentified = identifiedSchemas.includes(schema.id);
                        
                        return (
                          <div 
                            key={schema.id}
                            className={`rounded-lg border transition-all ${
                              isIdentified ? 'border-primary bg-primary/5' : 'bg-muted/30'
                            }`}
                          >
                            <div 
                              className="p-3 cursor-pointer"
                              onClick={() => toggleSchema(schema.id)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <ChevronRight className={`w-4 h-4 transition-transform ${isSchemaExpanded ? 'rotate-90' : ''}`} />
                                  <span className="font-medium">{schema.name}</span>
                                  {isIdentified && (
                                    <Badge variant="default" className="text-xs">Identified</Badge>
                                  )}
                                </div>
                                <Button
                                  size="sm"
                                  variant={isIdentified ? 'default' : 'outline'}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleIdentified(schema.id);
                                  }}
                                >
                                  {isIdentified ? 'Remove' : 'This resonates'}
                                </Button>
                              </div>
                              <p className="text-sm text-muted-foreground mt-1 italic">
                                "{schema.belief}"
                              </p>
                            </div>
                            
                            <AnimatePresence>
                              {isSchemaExpanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: 'auto', opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  className="overflow-hidden"
                                >
                                  <div className="p-4 pt-0 space-y-4">
                                    {/* Origin */}
                                    <div>
                                      <h5 className="text-sm font-medium flex items-center gap-1 mb-1">
                                        <Info className="w-3 h-3" /> How it may have developed
                                      </h5>
                                      <p className="text-xs text-muted-foreground">{schema.origin}</p>
                                    </div>

                                    {/* Triggers */}
                                    <div>
                                      <h5 className="text-sm font-medium mb-1">Common triggers</h5>
                                      <div className="flex flex-wrap gap-1">
                                        {schema.triggers.map(trigger => (
                                          <Badge key={trigger} variant="outline" className="text-xs">
                                            {trigger}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Coping */}
                                    <div>
                                      <h5 className="text-sm font-medium mb-1">How people cope (not always helpfully)</h5>
                                      <div className="flex flex-wrap gap-1">
                                        {schema.coping.map(cope => (
                                          <Badge key={cope} variant="secondary" className="text-xs">
                                            {cope}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>

                                    {/* Healthy Alternative */}
                                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/20">
                                      <h5 className="text-sm font-medium flex items-center gap-1 mb-1">
                                        <Sparkles className="w-3 h-3 text-green-500" /> Healthier perspective
                                      </h5>
                                      <p className="text-xs text-muted-foreground">{schema.healthyAlternative}</p>
                                    </div>

                                    {/* Self-Assessment Questions */}
                                    {showAssessment && (
                                      <div>
                                        <h5 className="text-sm font-medium mb-2">Reflection questions</h5>
                                        <ul className="space-y-1">
                                          {schema.questions.map((q, i) => (
                                            <li key={i} className="text-xs text-muted-foreground flex items-start gap-2">
                                              <span className="text-primary">•</span>
                                              {q}
                                            </li>
                                          ))}
                                        </ul>
                                      </div>
                                    )}

                                    {/* Personal Notes */}
                                    <div>
                                      <h5 className="text-sm font-medium mb-1">Your notes</h5>
                                      <Textarea
                                        placeholder="How does this show up in your life? Any specific memories or situations?"
                                        value={notes[schema.id] || ''}
                                        onChange={(e) => setNotes(prev => ({ ...prev, [schema.id]: e.target.value }))}
                                        className="min-h-[60px] text-xs"
                                        onClick={(e) => e.stopPropagation()}
                                      />
                                    </div>
                                  </div>
                                </motion.div>
                              )}
                            </AnimatePresence>
                          </div>
                        );
                      })}
                    </CardContent>
                  </motion.div>
                )}
              </AnimatePresence>
            </Card>
          );
        })}
      </div>

      {/* Premium Prompt */}
      <Card className="bg-gradient-to-br from-amber-500/5 to-orange-500/5 border-amber-500/20">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Sparkles className="w-5 h-5 text-amber-500 shrink-0" />
            <div>
              <h4 className="font-semibold text-sm">Unlock Deeper Exploration</h4>
              <p className="text-xs text-muted-foreground mt-1">
                With Premium Therappi, your identified schemas are connected to your life timeline 
                and thought patterns, helping you see connections between early experiences and 
                current challenges. Therappi can gently explore these patterns with you in conversation.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export { SCHEMA_DOMAINS };
